import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';

function WebshopPage({ addToCart }) {
  const { toast } = useToast();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    const fetchSkeljung = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/skeljung-products`);
        const data = await response.json();
        setProducts(data || []);
        setSelectedCategory('all');
      } catch (error) {
        console.error('Error fetching Skeljungur products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSkeljung();
  }, []);

  const handleAdd = (product) => {
    addToCart({
      id: product.id,
      name: product.name,
      price: product.price,
      category: 'vefverslun',
      supplier: 'skeljungur',
      external: true,
      image: product.image,
      url: product.url,
      quantity: 1,
    });

    toast({
      title: 'Bætt í körfu',
      description: product.name,
    });
  };

  const categoryLabels = {
    all: 'Allar vörur',
    bílahreinsiefni: 'Bílahreinsiefni',
    felguhreinsir: 'Felguhreinsar',
    hreinsiefni: 'Hreinsiefni',
    gluggahreinsir: 'Gluggahreinsir',
    shampoo: 'Sjampó',
    aukahlutir: 'Aukahlutir',
  };

  const availableCategories = ['all', ...Array.from(new Set((products || []).map((p) => p.category).filter(Boolean)))];
  const filteredProducts = selectedCategory === 'all'
    ? products
    : products.filter((p) => p.category === selectedCategory);

  return (
    <div className="min-h-screen py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-[#2C3E50] mb-4" style={{ fontFamily: 'Work Sans, sans-serif' }}>
            Vefverslun
          </h1>
          <p className="text-lg text-[#8B9BA8] max-w-2xl mx-auto">
            Bílaþrif og hreinsiefni frá vefverslun Skeljungs – keypt í gegnum Alvörubón.
          </p>
        </div>

        <Card className="border-[#E8ECEF]" data-testid="webshop-page">
          <CardHeader>
            <CardTitle className="text-2xl text-[#2C3E50]" style={{ fontFamily: 'Work Sans, sans-serif' }}>
              Vörur úr vefverslun Skeljungs
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {loading ? (
              <p className="text-[#8B9BA8] text-sm">Hleð vörum...</p>
            ) : (
              <>
                <div className="flex flex-wrap gap-2 mb-4" data-testid="webshop-category-filter">
                  {availableCategories.map((value) => (
                    <Button
                      key={value}
                      type="button"
                      variant={selectedCategory === value ? 'default' : 'outline'}
                      onClick={() => setSelectedCategory(value)}
                      className={selectedCategory === value ? 'bg-[#0A4D8C] text-white' : 'bg-white text-[#2C3E50]'}
                      size="sm"
                      data-testid={`webshop-category-${value}`}
                    >
                      {categoryLabels[value] || value}
                    </Button>
                  ))}
                </div>

                {filteredProducts.length === 0 ? (
                  <p className="text-[#8B9BA8] text-sm">Engar vörur í þessum flokki eins og er.</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredProducts.map((product) => (
                      <Card
                        key={product.id}
                        className="border-[#E8ECEF] flex flex-col"
                        data-testid="webshop-product-card"
                      >
                        <CardContent className="p-4 flex flex-col h-full">
                          <div className="w-full h-40 bg-[#F8FAFB] rounded-lg overflow-hidden mb-3">
                            {product.image ? (
                              <img
                                src={product.image}
                                alt={product.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-[#8B9BA8] text-sm">
                                Mynd ekki tiltæk
                              </div>
                            )}
                          </div>
                          <h3 className="text-base font-semibold text-[#2C3E50] mb-1">
                            {product.name}
                          </h3>
                          <p className="text-[#0A4D8C] font-bold mb-3">
                            {product.price?.toLocaleString()} kr.
                          </p>
                          <div className="mt-auto flex flex-col gap-2">
                            <Button
                              type="button"
                              onClick={() => handleAdd(product)}
                              className="w-full bg-[#0A4D8C] hover:bg-[#083A6B] text-white"
                              data-testid="webshop-add-to-cart"
                            >
                              Setja í körfu
                            </Button>
                            {product.url && (
                              <a
                                href={product.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-[#4A9FD8] hover:text-[#0A4D8C] underline text-center"
                                data-testid="webshop-product-link"
                              >
                                Skoða vöru hjá Skeljung
                              </a>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default WebshopPage;
